import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Badge, ProgressBar, Spinner } from 'react-bootstrap';
import AdvanceTable from '../base/AdvanceTable';
import AdvanceTableFooter from '../base/AdvanceTableFooter';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import SearchBox from '../common/SearchBox';
import { useAdvanceTableContext } from '../../providers/AdvanceTableProvider';
import { useSalesDeliveryTokens } from '../../features/orders/useSalesDashboard';
import EstimateProvider, { RowEstimate } from '../../features/machine-intervals/EstimateProvider.js';

const fmt = n => Number(n || 0).toLocaleString();

const statusBadge = status => {
  const s = String(status || '').toUpperCase();
  if (s === 'DONE' || s === 'COMPLETED') return { bg: 'success', label: 'Done' };
  if (s === 'IN_PROGRESS' || s === 'RUNNING') return { bg: 'info', label: 'In Progress' };
  if (s === 'PENDING') return { bg: 'warning', label: 'Pending' };
  return { bg: 'secondary', label: s || 'Unknown' };
};

const deliveryBadge = mode => {
  const m = String(mode || '').toUpperCase();
  if (m === 'COIL') return { bg: 'dark', label: 'Coil' };
  return { bg: 'primary', label: 'Drum' };
};

const estKey = r => r?.tokenId || `${r?.jobId || '?'}:${r?.productLabel || '?'}`;

const rowToEstimateToken = r => ({
  key: estKey(r),
  tokenId: r?.tokenId || null,
  machineId: r?.machineId || null,
  phase: r?.phase || null,
  productLabel: r?.productLabel || null,
  color: r?.color || null,
  plannedM: Number(r?.plannedLength) || 0,
  producedM: Number(r?.producedLength) || 0,
  started: (Number(r?.producedLength) || 0) > 0
});

const columns = [
  {
    accessorKey: 'orderCode',
    header: 'Order',
    cell: ({ row: { original } }) => (
      <div className="d-flex flex-column">
        <Link
          to={`/apps/sales-management/order-details/${original.orderId}`}
          className="text-decoration-none fw-bold fs-9"
          title={original.orderId}
        >
          {original.orderCode || String(original.orderId).slice(0, 8) + '...'}
        </Link>
        <span className="text-muted" style={{ fontSize: 10 }}>
          {original.client || '—'}
        </span>
      </div>
    ),
    meta: {
      cellProps: { className: 'py-3' },
      headerProps: { style: { width: '12%' } }
    }
  },
  {
    accessorKey: 'productLabel',
    header: 'Product',
    cell: ({ row: { original } }) => (
      <span className="fw-semibold fs-9">
        {original.productLabel || '—'}
      </span>
    ),
    meta: {
      cellProps: { className: 'py-3' },
      headerProps: { style: { width: '14%' } }
    }
  },
  {
    accessorKey: 'client',
    header: 'Client',
    enableHiding: true,
    meta: {
      cellProps: { className: 'd-none' },
      headerProps: { className: 'd-none' }
    }
  },
  {
    accessorKey: 'tokenId',
    header: 'Token',
    cell: ({ row: { original } }) => (
      <Link
        to={`/apps/project-management/project-details/${original.jobId}`}
        className="text-decoration-none fs-10 font-monospace fw-semibold"
        title={`Job: ${original.jobId || '—'}`}
      >
        {original.tokenId?.slice(0, 10) || '—'}
      </Link>
    ),
    meta: {
      cellProps: { className: 'py-3' },
      headerProps: { style: { width: '9%' } }
    }
  },
  {
    id: 'delivery',
    header: 'Delivery',
    cell: ({ row: { original } }) => {
      const d = deliveryBadge(original.deliveryMode);
      const specs = [
        original.drum_material,
        original.drum_diameter ? `Ø${original.drum_diameter}` : ''
      ]
        .filter(Boolean)
        .join(' ');

      return (
        <div className="d-flex flex-column">
          <Badge bg={d.bg} className="mb-1" style={{ width: 'fit-content' }}>
            {d.label}
          </Badge>
          {specs && (
            <span className="text-muted" style={{ fontSize: 10 }}>
              {specs}
            </span>
          )}
        </div>
      );
    },
    meta: {
      cellProps: { className: 'py-3' },
      headerProps: { style: { width: '10%' } }
    }
  },
  {
    accessorKey: 'plannedLength',
    header: 'Planned (m)',
    cell: ({ row: { original } }) => (
      <span className="fw-semibold">{fmt(original.plannedLength)}</span>
    ),
    meta: {
      cellProps: { className: 'text-end py-3' },
      headerProps: { style: { width: '9%' }, className: 'text-end' }
    }
  },
  {
    accessorKey: 'producedLength',
    header: 'Produced (m)',
    cell: ({ row: { original } }) => (
      <span className={original.producedLength > 0 ? 'fw-semibold text-success' : 'text-muted'}>
        {fmt(original.producedLength)}
      </span>
    ),
    meta: {
      cellProps: { className: 'text-end py-3' },
      headerProps: { style: { width: '9%' }, className: 'text-end' }
    }
  },
  {
    id: 'progress',
    header: 'Progress',
    cell: ({ row: { original } }) => {
      const pct = Math.min(100, Math.round(Number(original.progress) || 0));
      return (
        <div style={{ minWidth: 80 }}>
          <ProgressBar
            now={pct}
            variant={pct >= 100 ? 'success' : pct > 0 ? 'info' : 'secondary'}
            style={{ height: 8, borderRadius: 999 }}
          />
          <div className="text-muted text-center" style={{ fontSize: 10 }}>
            {pct}%
          </div>
        </div>
      );
    },
    meta: {
      cellProps: { className: 'py-3' },
      headerProps: { style: { width: '9%' } }
    }
  },
  {
    id: 'phase',
    header: 'Phase',
    cell: ({ row: { original } }) => (
      <Badge bg="info" text="dark" className="rounded-pill">
        {String(original.phase || '—').replaceAll('_', ' ')}
      </Badge>
    ),
    meta: {
      cellProps: { className: 'py-3' },
      headerProps: { style: { width: '10%' } }
    }
  },
  {
    id: 'status',
    header: 'Status',
    accessorFn: row => row.status,
    cell: ({ row: { original } }) => {
      const s = statusBadge(original.status);
      return <Badge bg={s.bg}>{s.label}</Badge>;
    },
    meta: {
      cellProps: { className: 'py-3' },
      headerProps: { style: { width: '8%' } }
    }
  },
  {
    id: 'estimate',
    header: 'Est. Left',
    enableSorting: false,
    cell: ({ row: { original } }) => <RowEstimate id={estKey(original)} compact />,
    meta: {
      cellProps: { className: 'py-3' },
      headerProps: { style: { width: '8%' } }
    }
  },
  {
    id: 'dueDate',
    header: 'Due',
    cell: ({ row: { original } }) => {
      const d = original.dueDate ? String(original.dueDate).slice(0, 10) : '—';
      return <span className="text-muted fs-10">{d}</span>;
    },
    meta: {
      cellProps: { className: 'py-3' },
      headerProps: { style: { width: '8%' } }
    }
  }
];

const TopSection = ({ count }) => {
  const { setGlobalFilter } = useAdvanceTableContext();
  return (
    <div className="d-flex flex-wrap mb-3 gap-3 align-items-center justify-content-between">
      <SearchBox
        placeholder="Search tokens, orders, products..."
        onChange={e => setGlobalFilter(e.target.value || undefined)}
        style={{ maxWidth: '24rem' }}
      />
      <span className="text-muted fs-9">{count} token(s) in production</span>
    </div>
  );
};

const SalesDeliveryTokensTable = () => {
  const { data, isLoading } = useSalesDeliveryTokens();
  const tokens = useMemo(() => data?.tokens || [], [data]);

  const table = useAdvanceTable({
    data: tokens,
    columns,
    pageSize: 8,
    pagination: true,
    sortable: true
  });

  if (isLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5">
        <Spinner animation="border" size="sm" className="me-2" />
        <span>Loading delivery tokens...</span>
      </div>
    );
  }

  if (!tokens.length) {
    return (
      <div className="text-muted text-center py-4">
        No final-phase tokens linked to orders yet.
      </div>
    );
  }

  return (
    <EstimateProvider rows={tokens} map={rowToEstimateToken}>
      <AdvanceTableProvider {...table}>
        <TopSection count={tokens.length} />
        <div className="border-bottom">
          <AdvanceTable
            tableProps={{
              className: 'phoenix-table border-top border-200 fs-9'
            }}
          />
          <AdvanceTableFooter pagination className="py-3" />
        </div>
      </AdvanceTableProvider>
    </EstimateProvider>
  );
};

export default SalesDeliveryTokensTable;
